import { type UserRole } from "@eisox/api/core-api";
import type { AuthState } from "@eisox/auth";
import { LoadingOverlay } from "@eisox/server-state-feedback";
import { TanStackDevtools } from "@tanstack/react-devtools";
import type { QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtoolsPanel } from "@tanstack/react-query-devtools";
import { createRootRouteWithContext, HeadContent, Outlet, useRouterState } from "@tanstack/react-router";
import { TanStackRouterDevtoolsPanel } from "@tanstack/react-router-devtools";

import { documentTitleHead } from "~/helpers/document-title.helper";
import type { Permission } from "~/types/auth.type";
import { belongsToOtherApp, leaveNow } from "~/utils/stack-guard.utils";

interface RouterContext {
  auth: AuthState;
  queryClient: QueryClient;
  permissions?: Permission;
  role?: UserRole;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  // Runs on in-app navigation only: the router never serves this app under /app.
  beforeLoad: async ({ location }) => {
    if (belongsToOtherApp(location.pathname)) await leaveNow();
  },
  head: () => documentTitleHead(),
  component: RootComponent,
});

function RootComponent() {
  const isNavigating = useRouterState({ select: state => state.status === "pending" });

  return (
    <>
      <HeadContent />
      <Outlet />
      <LoadingOverlay busy={isNavigating} />
      <TanStackDevtools
        config={{
          position: "bottom-right",
        }}
        plugins={[
          {
            name: "TanStack Router",
            render: <TanStackRouterDevtoolsPanel />,
          },
          {
            name: "Tanstack Query",
            render: <ReactQueryDevtoolsPanel />,
          },
        ]}
      />
    </>
  );
}
